import { Suspense, lazy, useEffect } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// components
import LoadingScreen from '../components/LoadingScreen';
import { analytics } from '../contexts/FirebaseContext';
import useAuth from '../hooks/useAuth';
import RoleBasedGuard from '../guards/RoleBasedGuard';
import LockerMainLayout from '../layouts/locker/LockerMainLayout';
import LicenciatarioDashboardNew from '../pages/dashboard/LicenciatarioDashboardNew';
import DashboardCorporativo from '../pages/dashboard/DashboardCorporativo';
import LockerGuard from '../guards/LockerGuard';
import { AudiosLockerProvider } from '../contexts/AudiosLockerContext';
import { analyticsEvents } from '../constants/analyticsEvents';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  const isDashboard = pathname.includes('/dashboard');

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed'
            })
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  const { user } = useAuth();

  const allowAll = false;

  useEffect(() => {
    analytics.setUserId(user?.id);
    analytics.logEvent(analyticsEvents.page_view);
  }, [user]);

  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          )
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          )
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'verify', element: <VerifyCode /> }
      ]
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/dashboard/paquetes/envios" replace /> },
        { path: '', element: <InicioDashboard /> },
        { path: 'app', element: <GeneralApp /> },
        {
          path: 'olimpo',
          children: [
            {
              path: '',
              element: (
                <RoleBasedGuard pageId="OLIMPO">
                  <Olimpo />
                </RoleBasedGuard>
              )
            },
            {
              path: 'reports',
              element: (
                <RoleBasedGuard pageId="OLIMPO_REPORTS">
                  <OlimpoReports />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'locker',
          element: (
            <RoleBasedGuard pageId="LOCKER">
              <Locker />
            </RoleBasedGuard>
          )
        },
        {
          path: 'administracion_lockers',
          element: (
            <RoleBasedGuard pageId="ADMINISTRACION_LOCKER">
              <AdministracionLocker />
            </RoleBasedGuard>
          )
        },
        {
          path: 'locker_operation_user',
          element: (
            <RoleBasedGuard pageId="OPERACION_LOCKER_USER">
              <LockerOperacionesUsuarios />
            </RoleBasedGuard>
          )
        },
        {
          path: 'locker_repartidor_reg',
          element: (
            <RoleBasedGuard pageId="REG_REPARTIDOR_LOCKER">
              <LockerListaEnviosCojidos />
            </RoleBasedGuard>
          )
        },

        {
          path: 'funcionesUser',
          element: (
            <RoleBasedGuard pageId="FUNCIONES_USER">
              <FuncionesUsuario />
            </RoleBasedGuard>
          )
        },

        {
          path: 'cotizaciones',
          children: [
            {
              element: (
                <RoleBasedGuard>
                  <Cotizaciones />
                </RoleBasedGuard>
              )
            },
            {
              path: 'new',
              element: (
                <RoleBasedGuard pageId="COTIZACION">
                  <CotizacionesNew />
                </RoleBasedGuard>
              )
            },
            {
              path: 'nuevoenvio',
              element: (
                <RoleBasedGuard pageId="ENVIO_NUEVO">
                  <CotiEnvio />
                </RoleBasedGuard>
              )
            },
            {
              path: 'envio/:idc',
              element: (
                <RoleBasedGuard>
                  <CotiChooseTarifa />
                </RoleBasedGuard>
              )
            },
            {
              path: 'administracion',
              element: (
                <RoleBasedGuard pageId="ADMINISTRACION">
                  <Hugo />
                </RoleBasedGuard>
              )
            },
            {
              path: 'empaque',
              element: (
                <RoleBasedGuard pageId="EMPAQUE">
                  <EmpaquePage />
                </RoleBasedGuard>
              )
            },
            {
              path: 'administracion/:name/:userId/edit',
              element: (
                <RoleBasedGuard>
                  <HugoEditarUser />
                </RoleBasedGuard>
              )
            },
            {
              path: 'users',
              element: (
                <RoleBasedGuard>
                  <Users />
                </RoleBasedGuard>
              )
            },
            { path: 'cart', element: <CotCart /> },
            { path: 'wallet', element: <DagWallet /> }
          ]
        },
        {
          path: 'administrativos',
          children: [
            {
              path: 'users',
              element: (
                <RoleBasedGuard>
                  <Users />
                </RoleBasedGuard>
              )
            },
            {
              path: 'licenciatarios',
              element: (
                <RoleBasedGuard>
                  <Users />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'paquetes',
          children: [
            {
              path: 'envios',
              element: (
                <RoleBasedGuard pageId="ENVIOS">
                  <PaqEnvios />
                </RoleBasedGuard>
              )
            },
            {
              path: 'productos',
              element: (
                <RoleBasedGuard pageId="EMPAQ_PRODUCTOS">
                  <EmpaqueProductos />
                </RoleBasedGuard>
              )
            },
            {
              path: 'productos/:id',
              element: (
                <RoleBasedGuard pageId="EMPAQ_PRODUCTOS_DETAIL">
                  <EmpaqueProductoDetail />
                </RoleBasedGuard>
              )
            },
            {
              path: 'envios/:id',
              element: (
                <RoleBasedGuard pageId="ENVIO_DETALLE">
                  <PaqDetalleEnvio />
                </RoleBasedGuard>
              )
            },
            {
              path: 'pickup',
              element: (
                <RoleBasedGuard pageId="PICKUP">
                  <PaqPickup />
                </RoleBasedGuard>
              )
            },
            {
              path: 'recoleccion',
              element: (
                <RoleBasedGuard pageId="RECOLECCION">
                  <PaqRecoleccion />
                </RoleBasedGuard>
              )
            },
            {
              path: 'recoleccion/detalle/:id',
              element: (
                <RoleBasedGuard>
                  <PaqDetalleRecoleccion />
                </RoleBasedGuard>
              )
            },
            {
              path: 'recoleccion/detalle_recibo/:id',
              element: (
                <RoleBasedGuard>
                  <PaqReciboDetalle />
                </RoleBasedGuard>
              )
            },
            {
              path: 'transacciones',
              element: (
                <RoleBasedGuard pageId="TRANSACCION">
                  <PaqTransacciones />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'servicios',
          children: [
            { path: '', element: <ServInicio /> },
            {
              path: 'licenciatarios',
              element: (
                <RoleBasedGuard>
                  <ServUsLicenciatarios />
                </RoleBasedGuard>
              )
            },
            {
              path: 'cliente_corporativo',
              element: (
                <RoleBasedGuard>
                  <ServUsClientCorp />
                </RoleBasedGuard>
              )
            },
            {
              path: 'puntos',
              element: (
                <RoleBasedGuard>
                  <ServUsPuntosDag />
                </RoleBasedGuard>
              )
            },
            {
              path: 'cajeros',
              element: (
                <RoleBasedGuard>
                  <ServUsCajeros />
                </RoleBasedGuard>
              )
            },
            {
              path: 'pagos-servicios',
              element: (
                <RoleBasedGuard pageId="PAGO_SERVICIO">
                  <PagosServicios />
                </RoleBasedGuard>
              )
            },
            {
              path: 'pagos-recargas',
              element: (
                <RoleBasedGuard pageId="RECARGA">
                  <PagosRecargas />
                </RoleBasedGuard>
              )
            },
            {
              path: 'serviciosRecargas',
              element: (
                <RoleBasedGuard pageId="SERVICIO-RECARGA">
                  <ServicioRecarga />
                </RoleBasedGuard>
              )
            },
            {
              path: 'serviciosRecargas/:id',
              element: (
                <RoleBasedGuard pageId="SERVICIO-RECARGA">
                  <ServicioRecargaDetalle />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'licenciatarios',
          children: [
            {
              path: 'asigned_users',
              element: (
                <RoleBasedGuard pageId="USUARIO_ASIGNADO">
                  <LicenciatarioUsers />
                </RoleBasedGuard>
              )
            },
            {
              path: 'caja',
              element: (
                <RoleBasedGuard pageId="CAJA">
                  <LicenciatarioCaja />
                </RoleBasedGuard>
              )
            },
            {
              path: 'mis_tickets',
              element: (
                <RoleBasedGuard pageId="MIS_TICKETS">
                  <MisTickets />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'crece',
          children: [
            {
              path: 'main',
              element: (
                <RoleBasedGuard pageId="CRECE_MAIN">
                  <CreceMain />
                </RoleBasedGuard>
              )
            },
            {
              path: 'sucursal',
              element: (
                <RoleBasedGuard pageId="CRECE_SUCURSAL">
                  <CreceSucursal />
                </RoleBasedGuard>
              )
            },
            {
              path: 'repartos',
              element: (
                <RoleBasedGuard pageId="CRECE_REPARTOS">
                  <CreceRepartos />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'ctas',
          element: (
            <RoleBasedGuard allowAll>
              <CTAs />
            </RoleBasedGuard>
          )
        },
        { path: 'ecommerce', element: <GeneralEcommerce /> },
        { path: 'analytics', element: <GeneralAnalytics /> },
        { path: 'banking', element: <GeneralBanking /> },
        { path: 'booking', element: <GeneralBooking /> },

        {
          path: 'e-commerce',
          children: [
            { element: <Navigate to="/dashboard/e-commerce/shop" replace /> },
            { path: 'shop', element: <EcommerceShop /> },
            { path: 'product/:name', element: <EcommerceProductDetails /> },
            { path: 'list', element: <EcommerceProductList /> },
            { path: 'product/new', element: <EcommerceProductCreate /> },
            { path: 'product/:name/edit', element: <EcommerceProductCreate /> },
            { path: 'checkout', element: <EcommerceCheckout /> },
            { path: 'invoice', element: <EcommerceInvoice /> }
          ]
        },
        {
          path: 'user',
          children: [
            { element: <Navigate to="/dashboard/user/profile" replace /> },
            { path: 'profile', element: <UserProfile /> },
            { path: 'cards', element: <UserCards /> },
            { path: 'list', element: <UserList /> },
            { path: 'new', element: <UserCreate /> },
            { path: ':name/edit', element: <UserCreate /> },
            { path: 'account', element: <UserAccount /> }
          ]
        },
        {
          path: 'blog',
          children: [
            { element: <Navigate to="/dashboard/blog/posts" replace /> },
            { path: 'posts', element: <BlogPosts /> },
            { path: 'post/:title', element: <BlogPost /> },
            { path: 'new-post', element: <BlogNewPost /> }
          ]
        },
        {
          path: 'mail',
          children: [
            { element: <Navigate to="/dashboard/mail/all" replace /> },
            { path: 'label/:customLabel', element: <Mail /> },
            { path: 'label/:customLabel/:mailId', element: <Mail /> },
            { path: ':systemLabel', element: <Mail /> },
            { path: ':systemLabel/:mailId', element: <Mail /> }
          ]
        },
        {
          path: 'chat',
          children: [
            { element: <Chat /> },
            { path: 'new', element: <Chat /> },
            { path: ':conversationKey', element: <Chat /> }
          ]
        },
        { path: 'calendar', element: <Calendar /> },
        { path: 'kanban', element: <Kanban /> }
      ]
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: 'pricing', element: <Pricing /> },
        { path: 'payment', element: <Payment /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    },
    { path: '/', element: <Enterprise /> },

    // {
    //   path: 'locker',
    //   element: <LockerMainLayout />,
    //   children: [
    //     { path: '', element: <LandingLocker /> },
    //     { path: 'cotizaciones/new', element: <CotizacionesNewLocker /> },
    //     { path: 'cotizaciones/nuevoenvio', element: <CotEnvioLocker /> },
    //     { path: 'cotizaciones/cart', element: <CotCartLocker /> }
    //   ]
    // },
    {
      path: 'locker/control',
      element: <LockerControl />
    },
    {
      path: 'locker',
      element: (
        <LockerGuard>
          <LandingLocker />
        </LockerGuard>
      )
    },
    {
      path: 'locker/sandbox',
      element: <Sandbox />
    },
    {
      path: 'locker/serviceProcces',
      // element: <LockerMainLayout />,
      children: [
        {
          path: '',
          element: (
            <LockerGuard>
              <AudiosLockerProvider>
                <LockerSeviceProcces />
              </AudiosLockerProvider>
            </LockerGuard>
          )
        },
        {
          path: 'final',
          element: (
            <LockerGuard>
              <LockerFlujoFinal />
            </LockerGuard>
          )
        }
      ]
    },
    {
      path: 'locker/recargaServicios',
      element: (
        <LockerGuard>
          <LockerRecargasServicios />
        </LockerGuard>
      )
    },
    {
      path: 'locker/flujo',
      element: <LockerMainLayout />,
      children: [
        { path: '', element: <LockerFlujo /> },
        { path: 'final', element: <LockerFlujoFinal /> }
      ]
    },
    {
      path: 'locker/cotizaciones',
      element: <LockerMainLayout />,
      children: [
        { path: 'new', element: <CotizacionesNewLocker /> },
        { path: 'nuevoenvio', element: <CotEnvioLocker /> },
        { path: 'cart', element: <CotCartLocker /> }
      ]
    },

    {
      path: '/a',
      element: <MainLayout />,
      children: [
        { element: <LandingPage /> },
        { path: 'about-us', element: <About /> },
        { path: 'contact-us', element: <Contact /> },
        { path: 'faqs', element: <Faqs /> },

        {
          path: 'components',
          children: [
            { element: <ComponentsOverview /> },
            // FOUNDATIONS
            { path: 'color', element: <Color /> },
            { path: 'typography', element: <Typography /> },
            { path: 'shadows', element: <Shadows /> },
            { path: 'grid', element: <Grid /> },
            { path: 'icons', element: <Icons /> },
            // MATERIAL UI
            { path: 'accordion', element: <Accordion /> },
            { path: 'alert', element: <Alert /> },
            { path: 'autocomplete', element: <Autocomplete /> },
            { path: 'avatar', element: <Avatar /> },
            { path: 'badge', element: <Badge /> },
            { path: 'breadcrumbs', element: <Breadcrumb /> },
            { path: 'buttons', element: <Buttons /> },
            { path: 'checkbox', element: <Checkbox /> },
            { path: 'chip', element: <Chip /> },
            { path: 'dialog', element: <Dialog /> },
            { path: 'label', element: <Label /> },
            { path: 'list', element: <List /> },
            { path: 'menu', element: <Menu /> },
            { path: 'pagination', element: <Pagination /> },
            { path: 'pickers', element: <Pickers /> },
            { path: 'popover', element: <Popover /> },
            { path: 'progress', element: <Progress /> },
            { path: 'radio-button', element: <RadioButtons /> },
            { path: 'rating', element: <Rating /> },
            { path: 'slider', element: <Slider /> },
            { path: 'snackbar', element: <Snackbar /> },
            { path: 'stepper', element: <Stepper /> },
            { path: 'switch', element: <Switches /> },
            { path: 'table', element: <Table /> },
            { path: 'tabs', element: <Tabs /> },
            { path: 'textfield', element: <Textfield /> },
            { path: 'timeline', element: <Timeline /> },
            { path: 'tooltip', element: <Tooltip /> },
            { path: 'transfer-list', element: <TransferList /> },
            { path: 'tree-view', element: <TreeView /> },
            { path: 'data-grid', element: <DataGrid /> },
            // EXTRA COMPONENTS
            { path: 'chart', element: <Charts /> },
            { path: 'map', element: <Map /> },
            { path: 'editor', element: <Editor /> },
            { path: 'copy-to-clipboard', element: <CopyToClipboard /> },
            { path: 'upload', element: <Upload /> },
            { path: 'carousel', element: <Carousel /> },
            { path: 'multi-language', element: <MultiLanguage /> },
            { path: 'animate', element: <Animate /> },
            { path: 'mega-menu', element: <MegaMenu /> },
            { path: 'form-validation', element: <FormValidation /> }
          ]
        }
      ]
    },
    {
      path: '/rastreo',
      element: <TrakingPage />
    },
    {
      path: '/rastreo/:codigo',
      element: <Tracking />
    },
    { path: '*', element: <Navigate to="/404" replace /> }
  ]);
}

// IMPORT COMPONENTS

const TrakingPage = Loadable(lazy(() => import('../pages/TrakingPage')));
const Tracking = Loadable(lazy(() => import('../pages/Tracking')));

// Authentication
const Login = Loadable(lazy(() => import('../pages/authentication/Login')));
const Register = Loadable(lazy(() => import('../pages/authentication/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/authentication/ResetPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/authentication/VerifyCode')));
// Dashboard
const InicioDashboard = Loadable(lazy(() => import('../pages/dashboard/InicioDashboard')));
const CotizacionesNew = Loadable(lazy(() => import('../pages/dashboard/CotizacionesNew')));
const Cotizaciones = Loadable(lazy(() => import('../pages/dashboard/Cotizaciones')));
const CotiEnvio = Loadable(lazy(() => import('../pages/dashboard/CotEnvio')));
const CotiChooseTarifa = Loadable(lazy(() => import('../pages/dashboard/CotChooseTarifa')));
const CotCart = Loadable(lazy(() => import('../pages/dashboard/CotCart')));
const DagWallet = Loadable(lazy(() => import('../pages/dashboard/DagWallet')));
const Hugo = Loadable(lazy(() => import('../pages/dashboard/Hugo')));
const Olimpo = Loadable(lazy(() => import('../pages/dashboard/Olimpo')));
const OlimpoReports = Loadable(lazy(() => import('../pages/dashboard/OlimpoReports')));
const Users = Loadable(lazy(() => import('../pages/dashboard/Users')));
const HugoEditarUser = Loadable(lazy(() => import('../pages/dashboard/HugoEditarUser')));
const PaqEnvios = Loadable(lazy(() => import('../pages/dashboard/PaqEnvios')));
const PaqRecoleccion = Loadable(lazy(() => import('../pages/dashboard/PaqRecoleccion')));
const PaqPickup = Loadable(lazy(() => import('../pages/dashboard/PaqPickUp')));
const PaqTransacciones = Loadable(lazy(() => import('../pages/dashboard/PaqTransacciones')));
const PaqDetalleEnvio = Loadable(lazy(() => import('../pages/dashboard/PaqDetalleEnvio')));
const PaqDetalleRecoleccion = Loadable(lazy(() => import('../pages/dashboard/PaqDetalleRecoleccion')));
const PaqReciboDetalle = Loadable(lazy(() => import('../pages/dashboard/PaqReciboDetalle')));
const ServInicio = Loadable(lazy(() => import('../pages/dashboard/ServInicio')));
const ServUsLicenciatarios = Loadable(lazy(() => import('../pages/dashboard/ServUsLicenciatarios')));
const ServUsClientCorp = Loadable(lazy(() => import('../pages/dashboard/ServUsClientCorp')));
const ServUsCajeros = Loadable(lazy(() => import('../pages/dashboard/ServUsCajeros')));
const ServUsPuntosDag = Loadable(lazy(() => import('../pages/dashboard/ServUsPuntosDag')));
const LockerControl = Loadable(lazy(() => import('../pages/control-locker/index')));
const LicenciatarioDashboard = Loadable(lazy(() => import('../pages/dashboard/LicenciatarioDashboard')));
const LicenciatarioUsers = Loadable(lazy(() => import('../pages/dashboard/LicenciatarioUsers')));
const LicenciatarioCaja = Loadable(lazy(() => import('../pages/dashboard/LicenciatarioCaja')));
const LicenciatarioDashboardCorporativo = Loadable(
  lazy(() => import('../pages/dashboard/LicenciatarioDashboardCorporativo'))
);
const MisTickets = Loadable(lazy(() => import('../pages/dashboard/MisTickets')));
// const CotiLista = Loadable(lazy(() => import('../pages/dashboard/CotLista')));

const FuncionesUsuario = Loadable(lazy(() => import('../pages/dashboard/FuncionesUsuario')));

const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));
const GeneralEcommerce = Loadable(lazy(() => import('../pages/dashboard/GeneralEcommerce')));
const GeneralAnalytics = Loadable(lazy(() => import('../pages/dashboard/GeneralAnalytics')));
const GeneralBanking = Loadable(lazy(() => import('../pages/dashboard/GeneralBanking')));
const GeneralBooking = Loadable(lazy(() => import('../pages/dashboard/GeneralBooking')));
const EcommerceShop = Loadable(lazy(() => import('../pages/dashboard/EcommerceShop')));
const EcommerceProductDetails = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductDetails')));
const EcommerceProductList = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductList')));
const EcommerceProductCreate = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductCreate')));
const EcommerceCheckout = Loadable(lazy(() => import('../pages/dashboard/EcommerceCheckout')));
const EcommerceInvoice = Loadable(lazy(() => import('../pages/dashboard/EcommerceInvoice')));
const BlogPosts = Loadable(lazy(() => import('../pages/dashboard/BlogPosts')));
const BlogPost = Loadable(lazy(() => import('../pages/dashboard/BlogPost')));
const BlogNewPost = Loadable(lazy(() => import('../pages/dashboard/BlogNewPost')));
const UserProfile = Loadable(lazy(() => import('../pages/dashboard/UserProfile')));
const UserCards = Loadable(lazy(() => import('../pages/dashboard/UserCards')));
const UserList = Loadable(lazy(() => import('../pages/dashboard/UserList')));
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));
const UserCreate = Loadable(lazy(() => import('../pages/dashboard/UserCreate')));
const Chat = Loadable(lazy(() => import('../pages/dashboard/Chat')));
const Mail = Loadable(lazy(() => import('../pages/dashboard/Mail')));
const Calendar = Loadable(lazy(() => import('../pages/dashboard/Calendar')));
const Kanban = Loadable(lazy(() => import('../pages/dashboard/Kanban')));
// Main
const LandingPage = Loadable(lazy(() => import('../pages/LandingPage')));
const About = Loadable(lazy(() => import('../pages/About')));
const Contact = Loadable(lazy(() => import('../pages/Contact')));
const Faqs = Loadable(lazy(() => import('../pages/Faqs')));
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Pricing = Loadable(lazy(() => import('../pages/Pricing')));
const Payment = Loadable(lazy(() => import('../pages/Payment')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
// Components
const ComponentsOverview = Loadable(lazy(() => import('../pages/ComponentsOverview')));
const Color = Loadable(lazy(() => import('../pages/components-overview/foundations/FoundationColors')));
const Typography = Loadable(lazy(() => import('../pages/components-overview/foundations/FoundationTypography')));
const Shadows = Loadable(lazy(() => import('../pages/components-overview/foundations/FoundationShadows')));
const Grid = Loadable(lazy(() => import('../pages/components-overview/foundations/FoundationGrid')));
const Icons = Loadable(lazy(() => import('../pages/components-overview/foundations/FoundationIcons')));
const Accordion = Loadable(lazy(() => import('../pages/components-overview/material-ui/Accordion')));
const Alert = Loadable(lazy(() => import('../pages/components-overview/material-ui/Alert')));
const Autocomplete = Loadable(lazy(() => import('../pages/components-overview/material-ui/Autocomplete')));
const Avatar = Loadable(lazy(() => import('../pages/components-overview/material-ui/Avatar')));
const Badge = Loadable(lazy(() => import('../pages/components-overview/material-ui/Badge')));
const Breadcrumb = Loadable(lazy(() => import('../pages/components-overview/material-ui/Breadcrumb')));
const Buttons = Loadable(lazy(() => import('../pages/components-overview/material-ui/buttons')));
const Checkbox = Loadable(lazy(() => import('../pages/components-overview/material-ui/Checkboxes')));
const Chip = Loadable(lazy(() => import('../pages/components-overview/material-ui/chips')));
const Dialog = Loadable(lazy(() => import('../pages/components-overview/material-ui/dialog')));
const Label = Loadable(lazy(() => import('../pages/components-overview/material-ui/Label')));
const List = Loadable(lazy(() => import('../pages/components-overview/material-ui/Lists')));
const Menu = Loadable(lazy(() => import('../pages/components-overview/material-ui/Menus')));
const Pagination = Loadable(lazy(() => import('../pages/components-overview/material-ui/Pagination')));
const Pickers = Loadable(lazy(() => import('../pages/components-overview/material-ui/pickers')));
const Popover = Loadable(lazy(() => import('../pages/components-overview/material-ui/Popover')));
const Progress = Loadable(lazy(() => import('../pages/components-overview/material-ui/progress')));
const RadioButtons = Loadable(lazy(() => import('../pages/components-overview/material-ui/RadioButtons')));
const Rating = Loadable(lazy(() => import('../pages/components-overview/material-ui/Rating')));
const Slider = Loadable(lazy(() => import('../pages/components-overview/material-ui/Slider')));
const Snackbar = Loadable(lazy(() => import('../pages/components-overview/material-ui/Snackbar')));
const Stepper = Loadable(lazy(() => import('../pages/components-overview/material-ui/stepper')));
const Switches = Loadable(lazy(() => import('../pages/components-overview/material-ui/Switches')));
const Table = Loadable(lazy(() => import('../pages/components-overview/material-ui/table')));
const Tabs = Loadable(lazy(() => import('../pages/components-overview/material-ui/Tabs')));
const Textfield = Loadable(lazy(() => import('../pages/components-overview/material-ui/textfield')));
const Timeline = Loadable(lazy(() => import('../pages/components-overview/material-ui/Timeline')));
const Tooltip = Loadable(lazy(() => import('../pages/components-overview/material-ui/Tooltip')));
const TransferList = Loadable(lazy(() => import('../pages/components-overview/material-ui/transfer-list')));
const TreeView = Loadable(lazy(() => import('../pages/components-overview/material-ui/TreeView')));
const DataGrid = Loadable(lazy(() => import('../pages/components-overview/material-ui/data-grid')));
//
const Charts = Loadable(lazy(() => import('../pages/components-overview/extra/Charts')));
const Map = Loadable(lazy(() => import('../pages/components-overview/extra/Map')));
const Editor = Loadable(lazy(() => import('../pages/components-overview/extra/Editor')));
const CopyToClipboard = Loadable(lazy(() => import('../pages/components-overview/extra/CopyToClipboard')));
const Upload = Loadable(lazy(() => import('../pages/components-overview/extra/Upload')));
const Carousel = Loadable(lazy(() => import('../pages/components-overview/extra/Carousel')));
const MultiLanguage = Loadable(lazy(() => import('../pages/components-overview/extra/MultiLanguage')));
const Animate = Loadable(lazy(() => import('../pages/components-overview/extra/animate')));
const MegaMenu = Loadable(lazy(() => import('../pages/components-overview/extra/MegaMenu')));
const FormValidation = Loadable(lazy(() => import('../pages/components-overview/extra/form-validation')));
const Enterprise = Loadable(lazy(() => import('../pages/Enterprise/Enterprise')));
const Locker = Loadable(lazy(() => import('../pages/dashboard/Locker')));
const Sandbox = Loadable(lazy(() => import('../pages/locker-page/Sandbox')));

const LandingLocker = Loadable(lazy(() => import('../pages/locker-page/Enterprise')));
const LockerFlujo = Loadable(lazy(() => import('../pages/locker-page/LockerFlujo')));
const LockerFlujoFinal = Loadable(lazy(() => import('../pages/locker-page/LockerFlujoFinal')));
const CotizacionesNewLocker = Loadable(lazy(() => import('../pages/locker-page/CotizacionesNewLocker')));
const CotEnvioLocker = Loadable(lazy(() => import('../pages/locker-page/CotEnvioLocker')));
const CotCartLocker = Loadable(lazy(() => import('../pages/locker-page/CotCartLocker')));
const LockerSeviceProcces = Loadable(lazy(() => import('../pages/locker-page/LockerSeviceProcces')));
const LockerRecargasServicios = Loadable(lazy(() => import('../pages/locker-page/LockerRecargasServicios')));

const PagosServicios = Loadable(lazy(() => import('../pages/dashboard/PagoServicios')));
const PagosRecargas = Loadable(lazy(() => import('../pages/dashboard/PagoRecargas')));
const ServicioRecarga = Loadable(lazy(() => import('../pages/dashboard/ServicioRecarga')));
const ServicioRecargaDetalle = Loadable(lazy(() => import('../pages/dashboard/ServicioRecargaDetalle')));

const EmpaquePage = Loadable(lazy(() => import('../pages/dashboard/EmpaquePage')));
const EmpaqueProductos = Loadable(lazy(() => import('../pages/dashboard/EmpaqueProductos')));
const EmpaqueProductoDetail = Loadable(lazy(() => import('../pages/dashboard/EmpaqueProductoDetail')));

const CreceMain = Loadable(lazy(() => import('../pages/dashboard/CreceMain')));
const CreceSucursal = Loadable(lazy(() => import('../pages/dashboard/CreceSucursal')));
const CreceRepartos = Loadable(lazy(() => import('../pages/dashboard/CreceRepartos')));

const AdministracionLocker = Loadable(lazy(() => import('../pages/dashboard/AdministracionLocker')));
const LockerOperacionesUsuarios = Loadable(lazy(() => import('../pages/dashboard/LockerOperacionesUsuarios')));
const LockerListaEnviosCojidos = Loadable(lazy(() => import('../pages/dashboard/LockerListaEnviosCojidos')));

const CTAs = Loadable(lazy(() => import('../pages/dashboard/CTAs')));
